import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icons } from '../utils/icons';
import api, { API_URL } from '../utils/api';
import { fontStack, btnPrimary, btnSecondary } from '../styles/shared';
import { useToast } from '../contexts/ToastContext';

function FilterDropdown({ value, onChange, placeholder, options }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find(o => o.value === value);

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '7px 12px', border: `1px solid ${value ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    background: value ? 'rgba(0,174,239,0.06)' : 'var(--card-bg)',
                    color: value ? 'var(--accent)' : 'var(--text-secondary)',
                    cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                }}
            >
                {selected ? selected.label : placeholder}
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
                    minWidth: '180px', maxHeight: '240px', overflowY: 'auto',
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    padding: '4px',
                }}>
                    {/* Reset / All option */}
                    <div
                        onClick={() => { onChange(''); setOpen(false); }}
                        style={{
                            padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                            fontWeight: !value ? 600 : 400, cursor: 'pointer',
                            color: !value ? 'var(--accent)' : 'var(--text-muted)',
                            background: !value ? 'rgba(0,174,239,0.06)' : 'transparent',
                            transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                        onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
                    >
                        All
                    </div>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: value === opt.value ? 600 : 400, cursor: 'pointer',
                                color: value === opt.value ? 'var(--accent)' : 'var(--text)',
                                background: value === opt.value ? 'rgba(0,174,239,0.06)' : 'transparent',
                                transition: 'background 0.1s',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}
                            onMouseEnter={e => { if (value !== opt.value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                            onMouseLeave={e => { if (value !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {opt.label}
                            {value === opt.value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ChattersTab({ wheels, isMobile, onViewEarnings, onStartChat }) {
    const toast = useToast();
    const [chatters, setChatters] = useState([]);
    const [chattersLoading, setChattersLoading] = useState(true);
    const [browseProfiles, setBrowseProfiles] = useState([]);
    const [browseLoading, setBrowseLoading] = useState(true);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [inviteCodes, setInviteCodes] = useState([]);
    const [codeChannels, setCodeChannels] = useState([]);

    // Approve request flow
    const [approvingRequestId, setApprovingRequestId] = useState(null);
    const [approveChannels, setApproveChannels] = useState([]);

    // Edit chatter form
    const [editingChatter, setEditingChatter] = useState(null);
    const [editChannels, setEditChannels] = useState([]);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // Discover filters
    const [searchQuery, setSearchQuery] = useState('');
    const [langFilter, setLangFilter] = useState('');
    const [tzFilter, setTzFilter] = useState('');
    const [pricingFilter, setPricingFilter] = useState('');
    const [sortBy, setSortBy] = useState('recommended');

    const loadAll = useCallback(async () => {
        setChattersLoading(true);
        setBrowseLoading(true);
        try {
            const [chattersRes, browseRes, requestsRes, codesRes] = await Promise.all([
                api.get('/api/admin/chatters').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/browse').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/requests').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/codes').catch(() => ({ data: {} })),
            ]);
            setChatters(chattersRes.data.chatters || []);
            setBrowseProfiles(browseRes.data.profiles || []);
            setPendingRequests(requestsRes.data.requests || []);
            setInviteCodes(codesRes.data.codes || []);
        } catch (e) {}
        setChattersLoading(false);
        setBrowseLoading(false);
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const respondToRequest = async (requestId, action, channels = []) => {
        try {
            await api.post('/api/admin/chatters/requests/respond', { request_id: requestId, action, channels });
            setApprovingRequestId(null);
            setApproveChannels([]);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    };

    const generateInviteCode = async () => {
        if (codeChannels.length === 0) return;
        try {
            await api.post('/api/admin/chatters/create-code', { channels: codeChannels });
            setCodeChannels([]);
            toast.success('Invite code generated!');
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to generate code'); }
    };

    const deleteInviteCode = async (codeId) => {
        try {
            await api.post('/api/admin/chatters/delete-code', { code_id: codeId });
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to delete code'); }
    };

    const toggleCodeChannel = (ch) => {
        setCodeChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
    };

    const deleteChatter = async (id, username) => {
        try {
            await api.post('/api/admin/chatters/delete', { chatter_id: id });
            setConfirmDeleteId(null);
            loadAll();
            toast.success(`@${username} removed`);
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to remove chatter'); }
    };

    const openEditChatter = (c) => {
        setEditingChatter(c);
        setEditChannels(c.channels || []);
    };

    const saveChatterChannels = async () => {
        if (!editingChatter) return;
        try {
            await api.post('/api/admin/chatters/update', {
                chatter_id: editingChatter.id,
                channels: editChannels,
            });
            setEditingChatter(null);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to save channels'); }
    };

    const removeChatterChannel = async (chatterId, channel, currentChannels) => {
        const newChannels = currentChannels.filter(c => c !== channel);
        try {
            await api.post('/api/admin/chatters/update', { chatter_id: chatterId, channels: newChannels });
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to update channels'); }
    };

    // Extract unique languages and timezones for filter dropdowns
    const allLanguages = useMemo(() => {
        const langs = new Set();
        browseProfiles.forEach(p => (p.languages || []).forEach(l => langs.add(l.lang)));
        return [...langs].sort();
    }, [browseProfiles]);

    const allTimezones = useMemo(() => {
        const tzs = new Set();
        browseProfiles.forEach(p => { if (p.timezone) tzs.add(p.timezone); });
        return [...tzs].sort();
    }, [browseProfiles]);

    // Filtered and sorted profiles
    const filteredProfiles = useMemo(() => {
        let results = [...browseProfiles];

        // Search
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            results = results.filter(p =>
                (p.display_name || '').toLowerCase().includes(q) ||
                (p.username || '').toLowerCase().includes(q)
            );
        }

        // Language filter
        if (langFilter) {
            results = results.filter(p =>
                (p.languages || []).some(l => l.lang === langFilter)
            );
        }

        // Timezone filter
        if (tzFilter) {
            results = results.filter(p => p.timezone === tzFilter);
        }

        // Pricing type filter
        if (pricingFilter) {
            results = results.filter(p => p.pricing_type === pricingFilter);
        }

        // Sort
        if (sortBy === 'lowest_hourly') {
            results.sort((a, b) => (a.proposed_hourly_rate || 999) - (b.proposed_hourly_rate || 999));
        } else if (sortBy === 'lowest_percentage') {
            results.sort((a, b) => (a.proposed_percentage || 999) - (b.proposed_percentage || 999));
        } else if (sortBy === 'newest') {
            results.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        // 'recommended' = default API order

        return results;
    }, [browseProfiles, searchQuery, langFilter, tzFilter, pricingFilter, sortBy]);

    const hasActiveFilters = searchQuery || langFilter || tzFilter || pricingFilter;

    const clearFilters = () => {
        setSearchQuery('');
        setLangFilter('');
        setTzFilter('');
        setPricingFilter('');
        setSortBy('recommended');
    };

    const sectionTitle = { fontSize: '16px', fontWeight: 700, color: 'var(--text)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '10px' };
    const card = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '14px', padding: isMobile ? '16px' : '20px', boxShadow: 'var(--card-shadow)', marginBottom: '20px' };

    const sortOptions = [
        { value: 'recommended', label: 'Recommended' },
        { value: 'lowest_hourly', label: 'Lowest hourly' },
        { value: 'lowest_percentage', label: 'Lowest commission' },
        { value: 'newest', label: 'Recently joined' },
    ];
    const pricingOptions = [
        { value: 'hourly', label: 'Hourly' },
        { value: 'percentage', label: 'Percentage' },
        { value: 'both', label: 'Both' },
        { value: 'negotiable', label: 'Negotiable' },
    ];
    const langOptions = useMemo(() => allLanguages.map(l => ({ value: l, label: l })), [allLanguages]);
    const tzOptions = useMemo(() => allTimezones.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') })), [allTimezones]);

    // Edit Chatter Modal
    if (editingChatter) {
        return (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Channels — @{editingChatter.username}</h3>
                        <button onClick={() => setEditingChatter(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>×</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' }}>
                        {wheels.map(w => (
                            <div key={w.channel_username} onClick={() => setEditChannels(prev => prev.includes(w.channel_username) ? prev.filter(c => c !== w.channel_username) : [...prev, w.channel_username])}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: editChannels.includes(w.channel_username) ? 'rgba(0,174,239,0.06)' : 'var(--surface-hover)', border: `1px solid ${editChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${editChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, background: editChannels.includes(w.channel_username) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {editChannels.includes(w.channel_username) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                </div>
                                <p style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', margin: 0 }}>{w.title || w.channel_username}</p>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditingChatter(null)} style={btnSecondary}>Cancel</button>
                        <button onClick={saveChatterChannels} style={{ ...btnPrimary, width: 'auto', padding: '10px 20px' }}>Save</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* ── Pending Requests ── */}
            {pendingRequests.length > 0 && (
                <div style={card}>
                    <h3 style={sectionTitle}>
                        <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}><Icons.Bell /></span>
                        Pending Requests
                        <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '10px', background: 'var(--surface-hover)', color: 'var(--text)', border: '1px solid var(--border)' }}>{pendingRequests.length}</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pendingRequests.map(req => (
                            <div key={req.id} style={{ padding: '12px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text)', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                                        {(req.chatter_username || 'C')[0].toUpperCase()}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>@{req.chatter_username}</p>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>Wants to join as your chatter</p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                        {approvingRequestId !== req.id && <>
                                            <button onClick={() => { setApprovingRequestId(req.id); setApproveChannels([]); }}
                                                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.35)', color: '#10b981', borderRadius: '8px', padding: '8px 18px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack, fontWeight: 600 }}>Approve</button>
                                            <button onClick={() => respondToRequest(req.id, 'reject')}
                                                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '8px', padding: '8px 18px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Reject</button>
                                            <button onClick={() => onStartChat(req.chatter_username, req.display_name)}
                                                style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '8px', padding: '8px 18px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Chat</button>
                                        </>}
                                        {approvingRequestId === req.id && <button onClick={() => setApprovingRequestId(null)}
                                            style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Cancel</button>}
                                    </div>
                                </div>
                                {approvingRequestId === req.id && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                                        <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px' }}>Select channels to give access to:</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                            {(wheels || []).map(w => (
                                                <label key={w.channel_username} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: `1px solid ${approveChannels.includes(w.channel_username) ? 'var(--text)' : 'var(--border)'}`, background: approveChannels.includes(w.channel_username) ? 'var(--surface-hover)' : 'var(--card-bg)', cursor: 'pointer', fontSize: '12px', fontWeight: approveChannels.includes(w.channel_username) ? 600 : 400, color: 'var(--text)', userSelect: 'none' }}>
                                                    <input type="checkbox" checked={approveChannels.includes(w.channel_username)}
                                                        onChange={e => setApproveChannels(prev => e.target.checked ? [...prev, w.channel_username] : prev.filter(c => c !== w.channel_username))}
                                                        style={{ display: 'none' }} />
                                                    {w.title || w.channel_username}
                                                </label>
                                            ))}
                                            {(wheels || []).length === 0 && <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>No channels available</p>}
                                        </div>
                                        <button onClick={() => respondToRequest(req.id, 'approve', approveChannels)}
                                            disabled={approveChannels.length === 0}
                                            style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', color: approveChannels.length === 0 ? 'var(--text-muted)' : 'var(--text)', borderRadius: '8px', padding: '8px 20px', fontSize: '12px', cursor: approveChannels.length === 0 ? 'not-allowed' : 'pointer', fontFamily: fontStack, fontWeight: 600 }}>
                                            Approve{approveChannels.length > 0 ? ` (${approveChannels.length} channel${approveChannels.length > 1 ? 's' : ''})` : ''}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── My Active Chatters ── */}
            <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h3 style={{ ...sectionTitle, margin: 0 }}>
                        <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}><Icons.Users /></span>
                        My Chatters
                        {chatters.length > 0 && <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>({chatters.length})</span>}
                    </h3>
                </div>

                {chattersLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}><img src={'/logo-light.webp'} alt="Loading" style={{ height: '24px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} /></div>
                ) : chatters.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                        <Icons.Users />
                        <p style={{ marginTop: '12px', fontSize: '14px' }}>No chatters yet</p>
                        <p style={{ fontSize: '12px', marginTop: '4px' }}>Generate an invite code below, or discover available chatters</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {chatters.map(c => (
                            <div key={c.id} style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '14px', padding: '14px 16px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), #0088cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '15px', fontWeight: 700 }}>
                                            {(c.display_name || c.username || 'C')[0].toUpperCase()}
                                        </div>
                                        {(() => {
                                            const isOnline = c.last_seen && (Date.now() - new Date(c.last_seen).getTime()) < 5 * 60 * 1000;
                                            return isOnline ? (
                                                <div style={{ position: 'absolute', bottom: 1, right: 1, width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', border: '2px solid var(--card-bg, #fff)' }} />
                                            ) : null;
                                        })()}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>{c.display_name || c.username}</p>
                                            {c.total_earned_usd > 0 && (
                                                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 600 }}>${c.total_earned_usd.toFixed(2)}</span>
                                            )}
                                        </div>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                                            {c.last_seen ? (() => {
                                                const isOnline = (Date.now() - new Date(c.last_seen).getTime()) < 5 * 60 * 1000;
                                                if (isOnline) return <span style={{ color: '#22c55e', fontWeight: 500 }}>Online</span>;
                                                const diff = Date.now() - new Date(c.last_seen).getTime();
                                                const mins = Math.floor(diff / 60000);
                                                const hours = Math.floor(mins / 60);
                                                const days = Math.floor(hours / 24);
                                                const ago = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : mins > 1 ? `${mins}m ago` : 'just now';
                                                return `Last seen ${ago}`;
                                            })() : '@' + c.username}
                                        </p>
                                        {/* Profile fields: timezone, languages, bio, pricing */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                                            {c.timezone && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    {c.timezone.replace(/_/g, ' ')}
                                                </span>
                                            )}
                                            {(c.languages || []).map((l, i) => {
                                                const lang = typeof l === 'string' ? l : l.lang;
                                                return <span key={i} style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(0,174,239,0.07)', color: 'var(--accent)', fontWeight: 500 }}>{lang}</span>;
                                            })}
                                            {c.pricing_type === 'hourly' && c.proposed_hourly_rate != null && (
                                                <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}>${c.proposed_hourly_rate}/hr</span>
                                            )}
                                            {c.pricing_type === 'percentage' && c.proposed_percentage != null && (
                                                <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontWeight: 600 }}>{c.proposed_percentage}%</span>
                                            )}
                                            {c.pricing_type === 'both' && (
                                                <>
                                                    {c.proposed_hourly_rate != null && <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}>${c.proposed_hourly_rate}/hr</span>}
                                                    {c.proposed_percentage != null && <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontWeight: 600 }}>{c.proposed_percentage}%</span>}
                                                </>
                                            )}
                                            {c.pricing_type === 'negotiable' && (
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Negotiable</span>
                                            )}
                                        </div>
                                        {c.bio && (
                                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {c.bio}
                                            </p>
                                        )}
                                        {(c.channels || []).length > 0 && (
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                                                {c.channels.map(ch => {
                                                    const wh = wheels.find(w => w.channel_username === ch);
                                                    return (
                                                        <span key={ch} onClick={(e) => { e.stopPropagation(); removeChatterChannel(c.id, ch, c.channels); }}
                                                            style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(0,174,239,0.08)', color: 'var(--accent)', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s' }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,174,239,0.08)'; e.currentTarget.style.color = 'var(--accent)'; }}>
                                                            {wh?.title || `@${ch}`} <span style={{ fontSize: '13px', lineHeight: 1 }}>×</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', ...(isMobile ? { marginLeft: '52px' } : {}) }}>
                                    <button onClick={() => onStartChat(c.username, c.display_name)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Icons.MessageCircle /> Chat</button>
                                    <button onClick={() => onViewEarnings(c)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Icons.Wallet /> Earnings</button>
                                    <button onClick={() => openEditChatter(c)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px' }}>Edit</button>
                                    {confirmDeleteId === c.id ? (
                                        <>
                                            <button onClick={() => deleteChatter(c.id, c.username)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack, fontWeight: 600 }}>Confirm</button>
                                            <button onClick={() => setConfirmDeleteId(null)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Cancel</button>
                                        </>
                                    ) : (
                                        <button onClick={() => setConfirmDeleteId(c.id)} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Remove</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Invite Codes ── */}
            <div style={card}>
                <h3 style={sectionTitle}>
                    <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}><Icons.Link /></span>
                    Invite Codes
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 14px' }}>Select channels and generate a single-use code that expires in 12 hours</p>
                {wheels.length === 0 ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Add a channel first.</p>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                            {wheels.map(w => (
                                <div key={w.channel_username} onClick={() => toggleCodeChannel(w.channel_username)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, transition: 'all 0.15s', background: codeChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--surface-hover)', color: codeChannels.includes(w.channel_username) ? '#fff' : 'var(--text-muted)', border: `1px solid ${codeChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}` }}>
                                    {w.title || w.channel_username}
                                </div>
                            ))}
                        </div>
                        <button onClick={generateInviteCode} disabled={codeChannels.length === 0} style={{ ...btnPrimary, width: 'auto', padding: '10px 24px', fontSize: '13px', opacity: codeChannels.length === 0 ? 0.5 : 1 }}>Generate Code</button>
                    </>
                )}
                {inviteCodes.length > 0 && (
                    <div style={{ marginTop: '18px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                        <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: '0 0 10px' }}>Active Codes</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {inviteCodes.map(ic => {
                                const isUsed = !!ic.used_by;
                                const isExpired = !isUsed && ic.expires_at && new Date(ic.expires_at) < new Date();
                                return (
                                    <div key={ic.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', opacity: (isUsed || isExpired) ? 0.5 : 1 }}>
                                        <code style={{ fontSize: '14px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '1px', fontFamily: 'monospace' }}>{ic.code}</code>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                {(ic.channels || []).map(ch => {
                                                    const wh = wheels.find(w => w.channel_username === ch);
                                                    return <span key={ch} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(0,174,239,0.08)', color: 'var(--accent)' }}>{wh?.title || ch}</span>;
                                                })}
                                            </div>
                                        </div>
                                        {isUsed ? (
                                            <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 500 }}>Used by @{ic.used_by}</span>
                                        ) : isExpired ? (
                                            <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 500 }}>Expired</span>
                                        ) : (
                                            <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 500 }}>Active</span>
                                        )}
                                        <div onClick={() => deleteInviteCode(ic.id)}
                                            style={{ padding: '4px', borderRadius: '6px', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}
                                            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}>
                                            <Icons.Trash />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Discover Available Chatters ── */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Discover Chatters</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px' }}>
                    <Icons.Users />
                    <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', margin: '12px 0 6px' }}>Coming Soon</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>The marketplace to discover and hire chatters is coming soon.</p>
                </div>
            </div>
        </div>
    );
}
